// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import MobiusAccordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import getAccordionVariantField, { AccordionVariant } from "@store/Framework/Common/Fields/Toolkit/accordionVariant";
import AnchoredAccordion from "@store/Library/Components/Toolkit/AnchoredAccordion";
import { AccordionSectionPresentationProps } from "@store/MobiusOverrides/AccordionSection/AccordionSection";
import ManagedAccordionSection from "@store/MobiusOverrides/AccordionSection/ManagedAccordionSection";
import { accordionFooterSectionProps } from "@store/Themes/common/props/accordionFooter";
import { accordionProductDetailsSectionProps } from "@store/Themes/common/props/accordionProductDetails";
import { DataOption, DataOptions } from "@toolkit/Framework/Common/Utilities/dataOptions";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import React, { FC, useMemo } from "react";

export type AccordionStyleOption = DataOption<AccordionSectionPresentationProps>;
export interface AccordionStyleOptionMapper {
    default: AccordionStyleOption;
    footer: AccordionStyleOption;
    productDetails: AccordionStyleOption;
}
export type AccordionStyleOptions = DataOptions<AccordionStyleOption> & AccordionStyleOptionMapper;
export type AccordionStyle = keyof AccordionStyleOptionMapper;

export const accordionStyleOptions: AccordionStyleOptions = {
    default: { id: "default", displayName: "Default", isDefault: true },
    footer: { id: "footer", displayName: "Footer", value: accordionFooterSectionProps },
    productDetails: {
        id: "productDetails",
        displayName: "Product Details",
        value: accordionProductDetailsSectionProps,
    },
};

const enum fields {
    headingLevel = "headingLevel",
    sections = "sections",
    style = "style",
}

const enum sectionFields {
    expanded = "expanded",
    id = "id",
    title = "title",
    anchor = "anchor",
}

const variantField = getAccordionVariantField(fields.style);

interface OwnProps extends WidgetProps {
    fields: {
        [fields.headingLevel]: number;
        [fields.sections]: [
            {
                fields: {
                    [sectionFields.expanded]: boolean;
                    [sectionFields.id]: string;
                    [sectionFields.title]: string;
                    [sectionFields.anchor]: string;
                };
            },
        ];
        [fields.style]: AccordionVariant;
    };
}

export interface AccordionStyles {
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
}

export const accordionStyles: AccordionStyles = {};

const Accordion: FC<OwnProps> = ({ fields: { headingLevel = 0, sections, style }, id }) => {
    const styles = useMemo(() => {
        return mergeProps<AccordionStyles>(accordionStyles, {
            accordionSection: variantField.parse(style),
        });
    }, [accordionStyles, style]);

    const accordionSections = sections.map(({ fields: section }, index) => {
        const { anchor, expanded, id: sectionId, title } = section;

        if (anchor) {
            return (
                <AnchoredAccordion
                    key={anchor}
                    anchorId={anchor}
                    initialExpanded={expanded}
                    title={title}
                    {...styles.accordionSection}
                >
                    <Zone contentId={id} zoneName={`AccordionSection-${sectionId || index}`} />
                </AnchoredAccordion>
            );
        }
        return (
            <ManagedAccordionSection
                key={sectionId || index}
                initialExpanded={expanded}
                title={title}
                {...styles.accordionSection}
            >
                <Zone contentId={id} zoneName={`AccordionSection-${sectionId || index}`} />
            </ManagedAccordionSection>
        );
    });

    if (!accordionSections.length) {
        return null;
    }

    return (
        <MobiusAccordion headingLevel={headingLevel} {...styles.accordion}>
            {accordionSections}
        </MobiusAccordion>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: Accordion,
    definition: {
        group: "Basic",
        displayName: "Accordion",
        icon: "list-dropdown",
        fieldDefinitions: [
            {
                name: fields.headingLevel,
                displayName: "Heading Level",
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                defaultValue: null,
                tooltip:
                    "Semantic heading level for accessibility (1-6), only set if accordions are to be treated as page headings",
                validate: x => (typeof x === "number" && (x < 1 || x > 6) ? "Value must be between 1 and 6" : ""),
            },
            {
                ...variantField.definition,
                displayName: "Style",
                fieldType: "Contextual",
            },
            {
                name: fields.sections,
                editorTemplate: "ListField",
                getDisplay: x => x.fields.title,
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: sectionFields.id,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        name: sectionFields.anchor,
                        displayName: "Anchor",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: sectionFields.expanded,
                        displayName: "Expand by default",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: sectionFields.title,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
